import React from 'react';

export interface ErrorAction {
  label: string;
  action: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
}

interface ErrorDisplayProps {
  title?: string;
  message: string;
  type?: 'error' | 'warning' | 'info';
  actions?: ErrorAction[];
  icon?: React.ReactNode;
  className?: string;
  showRetry?: boolean;
  onRetry?: () => void;
  context?: string;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  title,
  message,
  type = 'error',
  actions = [],
  icon,
  className = '',
  showRetry = true,
  onRetry,
  context
}) => {
  const getTypeStyles = () => {
    switch (type) {
      case 'warning':
        return {
          container: 'bg-yellow-50 border-yellow-200',
          border: 'border-l-yellow-500',
          icon: 'text-yellow-500',
          title: 'text-yellow-800',
          message: 'text-yellow-700'
        };
      case 'info':
        return {
          container: 'bg-blue-50 border-blue-200',
          border: 'border-l-blue-500',
          icon: 'text-blue-500',
          title: 'text-blue-800',
          message: 'text-blue-700'
        };
      default:
        return {
          container: 'bg-red-50 border-red-200',
          border: 'border-l-red-500',
          icon: 'text-red-500',
          title: 'text-red-800',
          message: 'text-red-700'
        };
    }
  };

  const getDefaultIcon = () => {
    switch (type) {
      case 'warning':
        return (
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'info':
        return (
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const getButtonStyles = (variant: string = 'primary') => {
    switch (variant) {
      case 'secondary':
        return 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300';
      case 'danger':
        return 'bg-red-600 text-white hover:bg-red-700';
      default:
        return 'bg-blue-600 text-white hover:bg-blue-700';
    }
  };

  const styles = getTypeStyles();
  const defaultActions: ErrorAction[] = [];

  if (showRetry && onRetry) {
    defaultActions.push({
      label: 'Réessayer',
      action: onRetry,
      variant: 'primary'
    });
  }

  const allActions = [...defaultActions, ...actions];

  return (
    <div className={`${styles.container} ${styles.border} border-l-4 p-4 rounded-md ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <div className={styles.icon}>
            {icon || getDefaultIcon()}
          </div>
        </div>
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={`text-sm font-medium ${styles.title} mb-1`}>
              {title}
            </h3>
          )}
          <p className={`text-sm ${styles.message}`}>
            {message}
          </p>
          {context && (
            <p className={`text-xs ${styles.message} mt-2 opacity-75`}>
              Contexte: {context}
            </p>
          )}
          {allActions.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {allActions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.action}
                  className={`px-3 py-2 text-xs font-medium rounded-md transition-colors duration-200 ${getButtonStyles(action.variant)}`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Composants spécialisés pour différents types d'erreurs
export const NetworkError: React.FC<Omit<ErrorDisplayProps, 'type' | 'title'>> = (props) => (
  <ErrorDisplay
    {...props}
    type="error"
    title="Erreur de connexion"
    message={props.message || "Impossible de se connecter au serveur. Vérifiez votre connexion internet."}
  />
);

export const ValidationError: React.FC<Omit<ErrorDisplayProps, 'type' | 'title'>> = (props) => (
  <ErrorDisplay
    {...props}
    type="warning"
    title="Données invalides"
    message={props.message || "Les données saisies ne sont pas valides."}
  />
);

export const AuthError: React.FC<Omit<ErrorDisplayProps, 'type' | 'title'>> = (props) => (
  <ErrorDisplay
    {...props}
    type="error"
    title="Erreur d'authentification"
    message={props.message || "Votre session a expiré. Veuillez vous reconnecter."}
  />
);

export const ServerError: React.FC<Omit<ErrorDisplayProps, 'type' | 'title'>> = (props) => (
  <ErrorDisplay
    {...props}
    type="error"
    title="Erreur serveur"
    message={props.message || "Une erreur est survenue sur le serveur. Veuillez réessayer plus tard."}
  />
);

export const NotFoundError: React.FC<Omit<ErrorDisplayProps, 'type' | 'title'>> = (props) => (
  <ErrorDisplay
    {...props}
    type="info"
    title="Ressource introuvable"
    message={props.message || "La ressource demandée n'a pas été trouvée."}
  />
);

export default ErrorDisplay;
